import CustomTooltip from '../../../shared/CustomTooltip';
import { Box, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
    useAddBillingExclusionMutation,
    useGetAllGuestsQuery,
    useGetBillingExclusionsQuery,
    useGetIndividualOwnerAssetsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import toast from '../../../shared/Toast';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import BillingExclusionForm from './BillingExclusionForm';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import EditIcon from '@mui/icons-material/Edit';
import { useWatch } from 'react-hook-form';
import isEqual from 'lodash/isEqual';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StripedDataGrid from '../../../shared/StripedDataGrid';
import DeleteIcon from '@mui/icons-material/Delete';

const BillingExclusionList = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    const [hasChanged, setHasChanged] = useState(false);
    const [currentExclusions, setCurrentExclusions] = useState<any[]>([]);

    const {
        data: getBillingExclusions,
        isFetching,
        refetch,
    } = useGetBillingExclusionsQuery(
        `/owners/${loggedInUser?.company}/billing-exclusions`,
        { skip: loggedInUser.role !== 'owner_admin' },
    );

    const { data: getAvailableModules, isLoading } =
        useGetIndividualOwnerAssetsQuery({
            url: `owners/${loggedInUser?.company}/modules`,
            status: 'published',
            sort: 'name',
        });

    const { data: allGuests } = useGetAllGuestsQuery(`/guests`);
    const [saveBillingExclusion] = useAddBillingExclusionMutation();

    const schema = z.object({
        exclusions: z.array(
            z.object({
                guest: z.object({
                    id: z.number().min(1, 'Guest is required'),
                }),
                modules: z.array(
                    z.object({
                        value: z.union([
                            z.number(),
                            z.literal('NONE'),
                            z.literal('ALL'),
                        ]),
                        label: z.string(),
                    }),
                ),
            }),
        ),
    });

    type BillingExclusions = z.infer<typeof schema>;

    const methods = useForm<BillingExclusions>({
        resolver: zodResolver(schema),
        defaultValues: { exclusions: [] },
    });

    const { control, setValue, handleSubmit, formState, reset } = methods;
    const exclusions = useWatch({ control, name: 'exclusions' });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'exclusions',
    });

    useEffect(() => {
        if (loggedInUser.role !== 'owner_admin') {
            navigate(`/permissionDenied`);
        }
    }, []);

    useEffect(() => {
        if (getBillingExclusions?.exclusions) {
            setCurrentExclusions(getBillingExclusions.exclusions);
        }
    }, [getBillingExclusions]);

    useEffect(() => {
        if (fields.length === 0 && exclusions.length === 0 && !isFetching) {
            append({ guest: { id: allGuests?.[0]?.id ?? 1 }, modules: [] });
        }
    }, [fields.length, exclusions.length, isFetching, append, allGuests]);

    useEffect(() => {
        if (!getBillingExclusions) return;

        const initialData = getBillingExclusions.exclusions.map((e: any) => ({
            guest: { id: e.guest.id },
            modules: e.modules.map((m: any) => ({
                value: m.id,
                label: m.name,
            })),
        }));

        const current = exclusions ?? [];

        const changed = !isEqual(current, initialData);

        setHasChanged(changed);
    }, [exclusions, getBillingExclusions]);

    const onSubmit = async (data: BillingExclusions) => {
        const allGuestIds = data.exclusions.map((e) => e.guest.id);
        const existingExclusions = getBillingExclusions?.exclusions || [];

        const newExclusions = data.exclusions
            .map((exclusion: any) => {
                const guest = allGuests?.find(
                    (g: any) => g.id === exclusion.guest.id,
                );
                const guestName = guest?.name ?? '';

                const isNoneSelected =
                    exclusion.modules.length === 1 &&
                    exclusion.modules[0]?.value === 'NONE';

                const isAll =
                    exclusion.modules.length === 1 &&
                    exclusion.modules[0]?.value === 'ALL';

                if (isNoneSelected) {
                    return null;
                }

                const modules = isAll
                    ? [] // Empty array means "All current & future"
                    : exclusion.modules.map((mod: any) => {
                          const fullModule = getAvailableModules?.find(
                              (m: any) => m.id === mod.value || m.id === mod.id,
                          );
                          return {
                              id: fullModule?.id ?? mod.value ?? mod.id,
                              name: fullModule?.name ?? '',
                          };
                      });

                return {
                    guest: {
                        id: guest?.id,
                        name: guestName,
                    },
                    modules,
                };
            })
            .filter(Boolean); // Remove nulls (i.e., "NONE" exclusions)

        const retainedExclusions = existingExclusions.filter(
            (e: any) => !allGuestIds.includes(e.guest.id),
        );

        const mergedExclusions = [...retainedExclusions, ...newExclusions];
        const payload = { exclusions: mergedExclusions };

        try {
            await saveBillingExclusion([
                loggedInUser?.company,
                payload,
            ]).unwrap();
            toast.success('Successfully saved billing exclusion information');

            const updated = await refetch();
            const latestExclusions = updated.data?.exclusions ?? [];
            setCurrentExclusions(latestExclusions);

            reset({
                exclusions: [
                    {
                        guest: { id: allGuests?.[0]?.id ?? 1 },
                        modules: [],
                    },
                ],
            });
        } catch (error: any) {
            toast.error(
                'Failed to save billing exclusion info. Please try again.',
            );
            console.error('Billing exclusion access error:', error);
        }
    };

    const columns = [
        {
            field: 'guestName',
            headerName: 'Guest',
            flex: 1,
        },
        {
            field: 'modules',
            headerName: 'Excluded Experiences',
            flex: 2,
            renderCell: (params: any) => {
                const modules = params.value;

                const firstTwo = modules.slice(0, 2);
                const remainingCount = modules.length - 2;

                const allAvailableMods =
                    getAvailableModules
                        ?.filter((m: any) => m.isAvailable)
                        .map((m: any) => ({
                            value: m.id,
                            label: m.name,
                            ...m,
                        })) || [];

                const firstTwoALL = allAvailableMods.slice(0, 2);
                const remainingCountALL = allAvailableMods.length - 2;

                if (!modules || modules.length === 0) {
                    return (
                        <Box display='flex' gap={0.5} flexWrap='wrap'>
                            {firstTwoALL.map((mod: any, i: number) => (
                                <CustomChip
                                    key={i}
                                    label={mod.name}
                                    size='small'
                                />
                            ))}
                            {remainingCountALL > 0 && (
                                <CustomTooltip
                                    title={
                                        <Box>
                                            {modules.map(
                                                (mod: any, i: number) => (
                                                    <Typography
                                                        key={i}
                                                        sx={{
                                                            whiteSpace:
                                                                'nowrap',
                                                        }}
                                                    >
                                                        {mod.name}
                                                    </Typography>
                                                ),
                                            )}
                                        </Box>
                                    }
                                >
                                    <CustomChip
                                        label={`+${
                                            allAvailableMods.length - 2
                                        }`}
                                        variant='outlined'
                                        size='small'
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </CustomTooltip>
                            )}
                            <CustomChip
                                key={'all'}
                                variant='outlined'
                                label='+ Future Listings'
                                size='small'
                            />
                        </Box>
                    );
                }

                return (
                    <Box display='flex' gap={0.5} flexWrap='wrap'>
                        {firstTwo.map((mod: any, i: number) => (
                            <CustomChip key={i} label={mod.name} size='small' />
                        ))}
                        {remainingCount > 0 && (
                            <CustomTooltip
                                title={
                                    <Box>
                                        {modules.map((mod: any, i: number) => (
                                            <Typography
                                                key={i}
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {mod.name}
                                            </Typography>
                                        ))}
                                    </Box>
                                }
                            >
                                <CustomChip
                                    label={`+${remainingCount}`}
                                    variant='outlined'
                                    size='small'
                                    sx={{ cursor: 'pointer' }}
                                />
                            </CustomTooltip>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'edit',
            headerName: 'Edit',
            width: 80,
            sortable: false,
            renderCell: (params: any) => {
                const { raw } = params.row;

                return (
                    <Box
                        onClick={() => {
                            reset({
                                exclusions: [
                                    {
                                        guest: { id: raw.guest.id },
                                        modules:
                                            raw.modules.length === 0
                                                ? [
                                                      {
                                                          value: 'ALL',
                                                          label: 'All Current and Future Listings',
                                                      },
                                                  ]
                                                : raw.modules.map((m: any) => ({
                                                      value: m.id,
                                                      label: m.name,
                                                  })),
                                    },
                                ],
                            });
                        }}
                        sx={{ cursor: 'pointer' }}
                    >
                        <EditIcon fontSize='small' sx={{ fontSize: '16px' }} />
                    </Box>
                );
            },
        },
        {
            field: 'delete',
            headerName: 'Delete',
            width: 60,
            sortable: false,
            renderCell: (params: any) => {
                const { raw } = params.row;
                return (
                    <Box
                        onClick={async () => {
                            const updated = currentExclusions.filter(
                                (ex: any) => ex.guest.id !== raw.guest.id,
                            );

                            const payload = { exclusions: updated };

                            try {
                                await saveBillingExclusion([
                                    loggedInUser?.company,
                                    payload,
                                ]).unwrap();

                                toast.success(
                                    `Removed billing exclusion for ${raw.guest.name}`,
                                );
                                const refreshed = await refetch();
                                const latestExclusions =
                                    refreshed.data?.exclusions ?? [];
                                setCurrentExclusions(latestExclusions);
                            } catch (err) {
                                toast.error(
                                    `Failed to remove billing exclusion. Please try again.`,
                                );
                                console.error(err);
                            }
                        }}
                        sx={{ cursor: 'pointer', color: 'error.main' }}
                    >
                        <DeleteIcon fontSize='small' />
                    </Box>
                );
            },
        },
    ];

    const rows = (getBillingExclusions?.exclusions || []).map(
        (ex: any, index: number) => ({
            id: index,
            guestName: ex.guest?.name,
            modules: ex.modules,
            raw: ex,
        }),
    );

    return isLoading || isFetching ? (
        <LoadingComponent />
    ) : (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={4}>
                    <Typography variant='h1'>Billing Exclusions</Typography>
                    <Typography
                        sx={{
                            textAlign: 'left',
                        }}
                    >
                        Applying a billing exclusion ensures all future bookings
                        for that guest are exempt from charges.
                        <CustomTooltip
                            title={
                                <Typography>
                                    <ul>
                                        {' '}
                                        <li>
                                            {' '}
                                            <strong>
                                                Exclude internal teams or VIPs:
                                            </strong>{' '}
                                            If you have staff, partners, or VIP
                                            guests using your experiences, you
                                            can exclude them from being
                                            charged—no need to adjust invoices
                                            manually later.{' '}
                                        </li>{' '}
                                        <li>
                                            {' '}
                                            <strong>
                                                Offer promotions or trials:
                                            </strong>{' '}
                                            Want to give a new guest company a
                                            free trial? You can easily exclude
                                            them from billing without removing
                                            the experience from your
                                            listings.{' '}
                                        </li>{' '}
                                        <li>
                                            {' '}
                                            <strong>
                                                Make invoicing easier:
                                            </strong>{' '}
                                            By setting exclusions in advance,
                                            you avoid manual edits or refunding
                                            charges after the fact.{' '}
                                        </li>{' '}
                                        <li>
                                            {' '}
                                            <strong>
                                                Stay in control of billing:
                                            </strong>{' '}
                                            Choose exactly who gets charged and
                                            for which experiences. It’s perfect
                                            if you’re offering both paid and
                                            complimentary options.{' '}
                                        </li>{' '}
                                    </ul>
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    </Typography>
                    <BillingExclusionForm
                        index={0}
                        handleDelete={() => {
                            remove(0);
                            // setIsEditing(false);
                        }}
                        allGuests={allGuests}
                        availableModules={getAvailableModules}
                        currentExclusions={currentExclusions}
                    />

                    <Box display='flex' justifyContent='flex-end' gap={2}>
                        <CustomButton
                            type='submit'
                            variant='contained'
                            color='primary'
                            loading={formState.isSubmitting}
                            disabled={formState.isSubmitting}
                        >
                            Save
                        </CustomButton>
                    </Box>

                    <Stack spacing={2} pt={2}>
                        <Typography variant='h2'>
                            Existing Exclusions
                        </Typography>
                        {rows.length > 0 ? (
                            <StripedDataGrid
                                getRowId={(row) => row.id}
                                columns={columns}
                                rows={rows}
                                sx={{ border: 0 }}
                                disableColumnFilter
                                disableColumnSelector
                                disableDensitySelector
                                autoHeight
                            />
                        ) : (
                            <Typography>No exclusions found.</Typography>
                        )}
                    </Stack>
                </Stack>
            </form>
        </FormProvider>
    );
};

export default BillingExclusionList;
